/**
 * Writing task pickers for Training Session entry (single-task open response).
 * Not Platform Scope — filter only. Omit = any task in inventory.
 */

import { TCF_WRITING_WORD_WINDOWS } from './exams/tcf/questionTypes';

export type WritingTaskOption = {
  /** Official task number on Question.taskNumber. */
  value: number;
  label: string;
  /** Short supporting line (word window / task role). */
  hint?: string;
  /**
   * Soft exam-prep time guide in minutes — not a hard Runtime deadline
   * (Training Session stays stop-anytime).
   */
  suggestedMinutes?: number;
};

const BY_EXAM: Record<string, WritingTaskOption[]> = {
  tcf: [
    {
      value: 1,
      label: 'Tâche 1',
      hint: `${TCF_WRITING_WORD_WINDOWS[1].minWordCount}–${TCF_WRITING_WORD_WINDOWS[1].maxWordCount} 词`,
      suggestedMinutes: 15,
    },
    {
      value: 2,
      label: 'Tâche 2',
      hint: `${TCF_WRITING_WORD_WINDOWS[2].minWordCount}–${TCF_WRITING_WORD_WINDOWS[2].maxWordCount} 词`,
      suggestedMinutes: 20,
    },
    {
      value: 3,
      label: 'Tâche 3',
      hint: `${TCF_WRITING_WORD_WINDOWS[3].minWordCount}–${TCF_WRITING_WORD_WINDOWS[3].maxWordCount} 词`,
      suggestedMinutes: 25,
    },
  ],
  ielts: [
    { value: 1, label: 'Task 1', hint: '≥150 词', suggestedMinutes: 20 },
    { value: 2, label: 'Task 2', hint: '≥250 词', suggestedMinutes: 40 },
  ],
  toefl: [
    { value: 1, label: 'Task 1', hint: '综合写作', suggestedMinutes: 20 },
    { value: 2, label: 'Task 2', hint: '独立写作', suggestedMinutes: 30 },
  ],
};

/** Empty when the exam has no multi-task writing picker (or no writing Skill). */
export function getWritingTaskOptionsForExam(
  examId: string | null | undefined
): WritingTaskOption[] {
  const key = String(examId ?? '')
    .trim()
    .toLowerCase();
  return BY_EXAM[key] ? [...BY_EXAM[key]] : [];
}

/**
 * Soft prep-time guide for a task (or exam default when task unknown).
 * Returns null when the exam has no writing task catalog.
 */
export function getWritingSuggestedMinutes(
  examId: string | null | undefined,
  taskNumber?: number | null
): number | null {
  const options = getWritingTaskOptionsForExam(examId);
  if (options.length === 0) return null;
  if (taskNumber != null && taskNumber > 0) {
    const match = options.find((option) => option.value === taskNumber);
    if (match?.suggestedMinutes != null) return match.suggestedMinutes;
  }
  // Unspecified task → first official task guide (usually Task 1 / Tâche 1).
  return options[0]?.suggestedMinutes ?? null;
}

export function isWritingTaskSupportedForExam(
  examId: string | null | undefined,
  taskNumber: number
): boolean {
  return getWritingTaskOptionsForExam(examId).some((option) => option.value === taskNumber);
}

/**
 * Parse optional task filter from query/UI.
 * Empty / NaN / unsupported → undefined (any task).
 */
export function normalizeOptionalWritingTaskNumber(
  examId: string | null | undefined,
  raw: string | number | null | undefined
): number | undefined {
  if (raw == null || raw === '') return undefined;
  const num = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(num) || num <= 0) return undefined;
  const taskNumber = Math.trunc(num);
  return isWritingTaskSupportedForExam(examId, taskNumber) ? taskNumber : undefined;
}
